import React from 'react';
import { Globe, Server, Database, Radio, Shield, AlertTriangle } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  percentage: number;
  color: string;
  icon: React.ElementType;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, percentage, color, icon: Icon }) => (
  <div className="bg-[#0F1629]/50 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50">
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{title}</span>
        <div className={`p-1.5 rounded-lg ${color}/10`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <div className={`text-3xl font-bold ${color}`}>{value}</div>
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-1 rounded-full bg-gray-800">
          <div
            className={`h-full rounded-full ${color}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-500">{percentage}%</span>
      </div>
    </div>
  </div>
);

interface AssetsMetricsProps {
  metrics: {
    web: number;
    servers: number;
    databases: number;
    network: number;
    vulnerable: number;
    secure: number;
    total: number;
  };
}

export const AssetsMetrics: React.FC<AssetsMetricsProps> = ({ metrics }) => {
  const calculatePercentage = (value: number) => 
    ((value / metrics.total) * 100).toFixed(1);

  const metricCards = [
    {
      title: 'Web Assets',
      value: metrics.web,
      percentage: Number(calculatePercentage(metrics.web)),
      color: 'text-blue-500',
      icon: Globe
    },
    {
      title: 'Servers',
      value: metrics.servers,
      percentage: Number(calculatePercentage(metrics.servers)),
      color: 'text-purple-500',
      icon: Server
    },
    {
      title: 'Databases',
      value: metrics.databases,
      percentage: Number(calculatePercentage(metrics.databases)),
      color: 'text-emerald-500',
      icon: Database
    },
    {
      title: 'Network',
      value: metrics.network,
      percentage: Number(calculatePercentage(metrics.network)),
      color: 'text-yellow-500',
      icon: Radio
    },
    {
      title: 'Vulnerable',
      value: metrics.vulnerable,
      percentage: Number(calculatePercentage(metrics.vulnerable)),
      color: 'text-red-500',
      icon: AlertTriangle
    },
    {
      title: 'Secure',
      value: metrics.secure,
      percentage: Number(calculatePercentage(metrics.secure)),
      color: 'text-emerald-500',
      icon: Shield
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {metricCards.map((card, index) => (
        <MetricCard key={index} {...card} />
      ))}
    </div>
  );
};